// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useMemo, useRef } from 'react';
import { DocsetDocument, frontendSlice, GetDocsetDocumentsRequest } from '../store/frontend/frontendSlice';
import { useAppDispatch, useAppSelector } from "./redux";
import { createSelector } from "@reduxjs/toolkit";
import { debounce } from "lodash";

type PageSubscription = {
    unsubscribe: () => void;
    offset: number;
}

export interface DocumentsPages {
    [offset: number]: DocsetDocument[];
}

interface IVirtualizedDocuments {
    isLoading: boolean;
    data?: DocumentsPages;
    totalCount?: number;
}

// Manages the subscriptions to retrieve document page queries
export function useVirtualizedDocuments(arg: GetDocsetDocumentsRequest | undefined, pageOffsets: number[]): IVirtualizedDocuments {
    const allPagesSelector = useMemo(() => !!arg ? createPagesSelector(arg, pageOffsets) : undefined, [arg, pageOffsets]);
    const data = useAppSelector(state => allPagesSelector ? allPagesSelector(state) : undefined);

    const subscriptions = useRef<PageSubscription[]>([]);
    const dispatch = useAppDispatch();

    useEffect(() => {
        // Clean up any existing subscriptions when the query arg changes and reset our subscription state
        const previousSubscriptions = subscriptions.current;
        subscriptions.current = []
        return () => {
            previousSubscriptions.forEach(subscription => subscription.unsubscribe());
        }
    }, [arg])

    const ensureSubscriptions = useMemo(() => debounce((request: GetDocsetDocumentsRequest, offsets: number[]) => {
        // Add any subscriptions we don't yet have
        for (const offset of offsets) {
            if (!subscriptions.current.some(sub => sub.offset === offset)) {
                const sub = dispatch(frontendSlice.endpoints.getDocumentsPage.initiate({ ...request, offset: offset }));
                subscriptions.current.push({
                    unsubscribe: () => sub.unsubscribe(),
                    offset: offset
                });
            }
        }

        // Remove any subscriptions that are no longer needed
        for (let i = subscriptions.current.length - 1; i >= 0; i--) {
            if (!offsets.includes(subscriptions.current[i].offset)) {
                subscriptions.current[i].unsubscribe();
                subscriptions.current.splice(i, 1);
            }
        }
    }, 500, { trailing: true }), [dispatch]);

    useEffect(() => {
        if (!!arg) {
            ensureSubscriptions(arg, pageOffsets)
        }
    }, [pageOffsets, arg, dispatch, ensureSubscriptions])

    return {
        isLoading: data?.total === undefined,
        data: data?.documents,
        totalCount: data?.total,
    }
}

const getDocumentsPageSelector = createSelector(
    [
        (request: GetDocsetDocumentsRequest, pageOffset: number) => request,
        (request: GetDocsetDocumentsRequest, pageOffset: number) => pageOffset,
    ],
    (request, pageOffset) => frontendSlice.endpoints.getDocumentsPage.select({ ...request, offset: pageOffset }),
    {
        memoizeOptions: {
            maxSize: 10
        }
    }
);

const createPagesSelector = (request: GetDocsetDocumentsRequest, pageOffsets: number[]) => createSelector(
    [
        ...pageOffsets.map(offset => getDocumentsPageSelector(request, offset))
    ],
    (...pagesQueryData) => {
        const pages: DocumentsPages = {};
        let total = undefined;

        for (let i = 0; i < pageOffsets.length; i++) {
            const pageData = pagesQueryData[i]?.data;
            if (!!pageData) {
                pages[pageOffsets[i]] = pageData.documents;
                total = pageData.total;
            }
        }

        return {
            documents: pages,
            total: total
        }
    }
);
